/**
 * Plugin collaborator adapters.
 *
 * These adapters wrap web-specific singletons (api, socketManager, auth context)
 * to implement the PluginCollaborators interfaces from @reflex/ui/client.
 * The web app mounts these via PluginClientProvider so plugins can use
 * contract-based hooks without importing web internals.
 */
import type {
  PluginApiClient,
  PluginRouterAdapter,
  PluginSocketLike,
  PluginAuthState,
} from '@reflex/ui/client/interfaces';
import type { WsServerMessage } from '@reflex/shared';
import { getSocketManager } from './socketManager';
import {
  getApiUrl,
  authHeaders,
  IS_CROSS_ORIGIN,
  getActiveOrganizationId,
  setActiveOrganizationId,
} from './api';
import { request } from '@reflex/ui/client/http';
import type { AuthState } from '@/hooks/auth/useAuth';

export function createApiAdapter(): PluginApiClient {
  return {
    async get<T>(path: string, options?: { headers?: Record<string, string> }): Promise<T> {
      return request(path, { method: 'GET', headers: options?.headers });
    },
    async post<T>(
      path: string,
      body?: unknown,
      options?: { headers?: Record<string, string> },
    ): Promise<T> {
      return request(path, {
        method: 'POST',
        body: body != null ? JSON.stringify(body) : undefined,
        headers: options?.headers,
      });
    },
    async put<T>(
      path: string,
      body?: unknown,
      options?: { headers?: Record<string, string> },
    ): Promise<T> {
      return request(path, {
        method: 'PUT',
        body: body != null ? JSON.stringify(body) : undefined,
        headers: options?.headers,
      });
    },
    async delete<T>(path: string, options?: { headers?: Record<string, string> }): Promise<T> {
      return request(path, { method: 'DELETE', headers: options?.headers });
    },
    request,
    getApiUrl,
    authHeaders,
    isCrossOrigin: IS_CROSS_ORIGIN,
    getActiveOrganizationId,
  };
}

export function createSocketAdapter(): PluginSocketLike {
  const socket = getSocketManager();
  return {
    subscribe(streamId: string) {
      socket.subscribe(streamId);
    },
    unsubscribe(streamId: string) {
      socket.unsubscribe(streamId);
    },
    onMessage(handler: (message: WsServerMessage) => void): () => void {
      return socket.onMessage(handler);
    },
    get connected() {
      return socket.state === 'open';
    },
    onConnectionChange(handler: (connected: boolean) => void): () => void {
      return socket.onStateChange((state) => {
        handler(state === 'open');
      });
    },
    addReconnectCallback(callback: () => void): void {
      socket.addReconnectCallback(callback);
    },
    removeReconnectCallback(callback: () => void): void {
      socket.removeReconnectCallback(callback);
    },
  };
}

export function createRouterAdapter(
  params: Record<string, string | undefined>,
  pathname: string,
  navigate: (to: string, options?: { replace?: boolean }) => void,
): PluginRouterAdapter {
  return { params, pathname, navigate };
}

export function createAuthAdapter(auth: AuthState): PluginAuthState {
  return {
    authenticated: auth.authenticated,
    loading: auth.loading,
    user: auth.user,
    activeOrganizationId: auth.activeOrganizationId,
    memberships: auth.memberships,
    activeMembership: auth.activeMembership,
    permissions: auth.permissions,
    isSuperAdmin: auth.isSuperAdmin,
    isAppAdmin: auth.isAppAdmin,
    isPlatformAdmin: auth.isPlatformAdmin,
    setupRequired: auth.setupRequired,
    isProductionEnv: auth.isProductionEnv,
    authEnabled: auth.authEnabled,
    can: auth.can,
    setActiveOrganizationId: (id: string | null) => {
      setActiveOrganizationId(id);
      auth.setActiveOrganizationId(id);
    },
    logout: auth.logout,
    refreshSession: auth.refreshSession,
  };
}
